import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export const StoreProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [sound, setSound] = useState(null);
  const [collections, setCollections] = useState([]);
  const [bag, setBag] = useState(() => load("nba_bag", []));
  const [saved, setSaved] = useState(() => load("nba_saved", []));
  const [bagOpen, setBagOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [savedHydrated, setSavedHydrated] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => { setSettings(r.data.settings); setSound(r.data.sound); }).catch(() => setSettings({}));
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  // merge server-side saved pieces on login (cross-device)
  useEffect(() => {
    if (!user) { setSavedHydrated(false); return; }
    api.get("/account/saved").then((r) => {
      setSaved((local) => {
        const merged = Array.from(new Set([...(r.data || []), ...local]));
        api.put("/account/saved", { saved: merged }).catch(() => {});
        return merged;
      });
      setSavedHydrated(true);
    }).catch(() => setSavedHydrated(true));
  }, [user]);

  useEffect(() => localStorage.setItem("nba_bag", JSON.stringify(bag)), [bag]);
  useEffect(() => {
    localStorage.setItem("nba_saved", JSON.stringify(saved));
    if (user && savedHydrated) api.put("/account/saved", { saved }).catch(() => {});
  }, [saved, user, savedHydrated]);

  const addToBag = useCallback((item) => {
    setBag((prev) => {
      const key = `${item.productId}-${item.colour}-${item.size}`;
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => i.key === key ? { ...i, qty: i.qty + item.qty } : i);
      return [...prev, { ...item, key }];
    });
    setBagOpen(true);
  }, []);

  const removeFromBag = useCallback((key) => setBag((p) => p.filter((i) => i.key !== key)), []);
  const updateQty = useCallback((key, qty) =>
    setBag((p) => p.map((i) => i.key === key ? { ...i, qty: Math.max(1, qty) } : i)), []);

  const toggleSaved = useCallback((slug) => {
    setSaved((p) => p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]);
  }, []);

  const bagCount = bag.reduce((n, i) => n + i.qty, 0);

  return (
    <StoreContext.Provider value={{
      settings, sound, collections, bag, setBag, saved, bagOpen, setBagOpen, searchOpen, setSearchOpen,
      addToBag, removeFromBag, updateQty, toggleSaved, bagCount,
    }}>
      {children}
    </StoreContext.Provider>
  );
};
